import { useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { useSearchParams } from "react-router-dom";
import { Plug } from "lucide-react";
import {
  Alert,
  Button,
  DataTable,
  EmptyState,
  Input,
  Pagination,
  Select,
  Skeleton,
  StatusPill,
  TruncatedText,
  useCursorPagination,
  type DataTableColumn,
} from "@playbookai/ui";
import type {
  IntegrationConnectionDetail,
  ConnectableIntegration,
} from "@playbookai/api-clients";
import {
  SETTINGS_CONNECTIONS_ROUTE,
  SETTINGS_PLAYER_AI_CONNECTED_APPS_ROUTE,
} from "../../routes.js";
import { useGuardedNavigate } from "../../settings/leave-guard.js";
import {
  useConnectionTools,
  useConnectionToolsApi,
} from "./use-connection-tools.js";

export function connectionIdentity(
  connection: IntegrationConnectionDetail,
  integration?: ConnectableIntegration,
): string {
  return (
    [integration?.displayName, connection.externalPrincipalName]
      .filter(Boolean)
      .join(" · ") || connection.displayName
  );
}
export function ConnectionStatus({
  connection,
}: {
  connection: IntegrationConnectionDetail;
}) {
  return (
    <StatusPill tone={connection.status === "active" ? "healthy" : "neutral"}>
      {connection.status === "active"
        ? "Connected"
        : connection.status === "revoked"
          ? "Disconnected"
          : connection.status.replaceAll("_", " ")}
    </StatusPill>
  );
}
export function PlayerAIConnectedApps() {
  const { api, scope } = useConnectionToolsApi();
  const [params, setParams] = useSearchParams();
  const search = params.get("search") ?? "";
  const pageSize = parseConnectionPageSize(params.get("pageSize"));
  const paging = useCursorPagination(`${search}|${pageSize}`, { pageSize });
  const navigate = useGuardedNavigate();
  const connections = useQuery({
    queryKey: [...scope, "connections-page", search, pageSize, paging.cursor],
    queryFn: () =>
      api.pageMine({
        ...(paging.cursor ? { cursor: paging.cursor } : {}),
        limit: pageSize,
        ...(search ? { query: search } : {}),
        transportMode: "mcp",
      }),
    retry: false,
  });
  const integrations = useQuery({
    queryKey: [...scope, "integrations"],
    queryFn: () => api.listConnectable(),
    retry: false,
  });
  paging.reportTotal(connections.data?.totalCount);
  const rows = connections.data?.data ?? [];
  const displayRows = rows.filter((connection) =>
    `${connection.displayName} ${connectionIdentity(
      connection,
      integrations.data?.find(
        (item) => item.id === connection.tenantIntegrationId,
      ),
    )}`
      .toLocaleLowerCase()
      .includes(search.toLocaleLowerCase()),
  );
  const columns: ReadonlyArray<DataTableColumn<IntegrationConnectionDetail>> = [
    {
      id: "connection",
      /* Column model (PB-5436): Connection takes what is left (no width, a
         176px floor) and the pill, count and button columns are fixed to their
         content. At 55% the pinned Connection column filled most of a 444px box
         beside the Player AI rail, leaving the rest ~60px to scroll through;
         now a box under 512px scrolls with the pinned column at 176px, and a
         wide box looks as before give or take a few pixels per column. */
      minWidth: 176,
      header: "Connection",
      cardRole: "title",
      cell: (connection) => (
        <div className="flex min-w-0 items-center gap-3">
          <Plug className="size-5 shrink-0 text-pb-text-muted" aria-hidden />
          {/* Clipped names show in the design-system Tooltip (d1), not a
              native title. */}
          <div className="min-w-0">
            <p className="font-medium text-pb-text">
              <TruncatedText>{connection.displayName}</TruncatedText>
            </p>
            <p className="max-w-64 text-caption text-pb-text-muted">
              <TruncatedText>
                {connectionIdentity(
                  connection,
                  integrations.data?.find(
                    (integration) =>
                      integration.id === connection.tenantIntegrationId,
                  ),
                )}
              </TruncatedText>
            </p>
          </div>
        </div>
      ),
    },
    {
      id: "status",
      width: "7rem",
      minWidth: 112,
      header: "Status",
      cell: (connection) => <ConnectionStatus connection={connection} />,
    },
    {
      id: "tools",
      width: "6.5rem",
      minWidth: 104,
      header: "Available tools",
      cell: (connection) => (
        <ConnectionToolsAvailability connectionId={connection.id} />
      ),
    },
    {
      id: "actions",
      width: "7.5rem",
      minWidth: 120,
      header: "Actions",
      align: "end",
      cardRole: "actions",
      cell: (connection) => (
        <Button
          size="sm"
          variant="secondary"
          className="w-max shrink-0 whitespace-nowrap"
          onClick={() =>
            navigate(
              `${SETTINGS_PLAYER_AI_CONNECTED_APPS_ROUTE}/${connection.id}${search ? `?search=${encodeURIComponent(search)}` : ""}`,
            )
          }
          aria-label={`View tool access for ${connection.displayName}`}
        >
          View access
        </Button>
      ),
    },
  ];
  // Keep old Your connections deep links working while the dedicated route becomes canonical.
  const focused = params.get("connectionId");
  useEffect(() => {
    if (focused)
      navigate(
        `${SETTINGS_PLAYER_AI_CONNECTED_APPS_ROUTE}/${encodeURIComponent(focused)}`,
      );
  }, [focused, navigate]);
  return (
    <div className="space-y-4">
      {/* Kept hand-built: SectionHeader spaces the title 4px above a
          max-w-xl text-sm line and adds mb-6, a look change no PR 3 row
          approves. */}
      <div>
        <h2 className="text-heading-3 text-pb-text">Connected apps</h2>
        <p className="mt-1 text-body-small text-pb-text-secondary">
          Connect an account once. Player AI starts with your workspace policy,
          and you can choose a different policy for each connection.
        </p>
      </div>
      <Input
        aria-label="Search connections"
        placeholder="Search connections"
        value={search}
        onChange={(e) => {
          const next = new URLSearchParams(params);
          next.set("search", e.target.value);
          setParams(next, { replace: true });
        }}
      />
      {connections.isPending && <Skeleton className="h-24" />}
      {connections.isError && (
        <Alert tone="critical" title="Could not load connected apps">
          <Button size="sm" onClick={() => void connections.refetch()}>
            Retry
          </Button>
        </Alert>
      )}
      {connections.isSuccess && rows.length === 0 && !search && (
        <EmptyState
          title="No connected apps"
          description="Connect an account to use the tools enabled for your workspace."
          action={
            <Button onClick={() => navigate(SETTINGS_CONNECTIONS_ROUTE)}>
              Manage your connections
            </Button>
          }
        />
      )}
      {connections.isSuccess && displayRows.length === 0 && search && (
        <EmptyState
          title="No matching connections"
          description="Try another name, site or account."
        />
      )}
      {displayRows.length > 0 && (
        <DataTable
          layout="fixed"
          columns={columns}
          data={displayRows}
          getRowId={(connection) => connection.id}
          caption="Connected apps"
          textSize="small"
          density="compact"
          pinFirstColumn
        />
      )}
      {connections.isSuccess && displayRows.length > 0 && (
        <div className="flex flex-wrap items-center justify-between gap-3">
          <Select
            ariaLabel="Connected apps per page"
            value={String(pageSize)}
            options={[12, 24, 48].map((size) => ({
              value: String(size),
              label: `${size} per page`,
            }))}
            onChange={(value) => {
              const next = new URLSearchParams(params);
              value === "12"
                ? next.delete("pageSize")
                : next.set("pageSize", value);
              setParams(next, { replace: true });
            }}
            className="w-36"
          />
          {paging.canGoBack || connections.data.hasMore ? (
            <Pagination
              mode="cursor"
              page={paging.page}
              pageItemCount={displayRows.length}
              totalCount={paging.totalCount}
              canGoBack={paging.canGoBack}
              hasNext={connections.data.hasMore}
              busy={connections.isFetching}
              onPrev={paging.goBack}
              onNext={() => {
                if (connections.data.nextCursor)
                  paging.goNext(connections.data.nextCursor);
              }}
            />
          ) : null}
        </div>
      )}
    </div>
  );
}

function parseConnectionPageSize(value: string | null): 12 | 24 | 48 {
  return value === "24" || value === "48" ? (Number(value) as 24 | 48) : 12;
}
function ConnectionToolsAvailability({
  connectionId,
}: {
  connectionId: string;
}) {
  const { query } = useConnectionTools(connectionId);
  // Kept as today's cell text: a Skeleton or Spinner in the cell is a look
  // change no PR 3 row approves.
  if (query.isPending) return "Loading…";
  if (query.isError) return "Unavailable";
  return `${query.data.filter((tool) => tool.playerAiInEffect).length} of ${query.data.length}`;
}
